import React, { useEffect, useMemo, useState } from "react"
import { ChevronOutlineRightIcon, DownloadExcel, DownloadIcon } from "../../components/Icons/iconImages";
import { Dropdown, MenuProps, Pagination, Select, Space } from 'antd';
import { Button } from "../../components/Buttons";

import {
    useGetAnalyticsDataQuery,
    useGetAnalyticStatusQuery,
    useGetListAccountNamesQuery,
    useLazyGetAccountNamesAfterSelectionQuery,
    useGetListAnalyticsAccountsQuery,
    useGetMetaDataQuery,
    useGetShopifyDataQuery,
    useGetYoutubeDataQuery,
    useLazyDownloadAnalyticsDataQuery,
    useLazyListYoutubeQuery,
    useGetShopifyUserDataQuery,
    useGetGoogleAdsDataQuery,
    useGetListGoogleAdsAccountNamesQuery,
    useLazyDownloadShopifyDataQuery,
    useGetListMetaAccountsQuery,
    useLazyDownloadMetaDataQuery,
    useLazyDownloadGoogleAdsDataQuery,
    useGetShopifyStatusQuery,
    useUpdateBigQueryIntegrationDatasetMutation,
    useGetTableNameAfterViewSchemaQuery,
    useGetTableSchemaFromDbQuery,
    useUpdateTableSchemaDescMutation,
    useCheckSchemaDescQuery
} from "../../services/integrationSlice";
import { useLocation, useParams } from "react-router-dom";

import './styles.css';
import { useForm } from "react-hook-form";
import DynamicTable from "../../components/Table/NestedTable";
import { toast } from "../../components/Toast/toast";

interface ViewRawDataProps {
    integrationId?: 'googleanalytics' | 'shopify' | 'meta' | 'youtube' | 'none';
    dataSource?: any;
}


const ViewRawData: React.FC<ViewRawDataProps> = () => {
    const { integrationId } = useParams();
    
    const { data: getStatus } = useGetAnalyticStatusQuery(""); // CHECK IF THE INTEGRATION IS ACTIVE
    const { data: listAnalyticsData } = useGetListAnalyticsAccountsQuery({}, {
        skip: integrationId !== 'googleanalytics',
        refetchOnMountOrArgChange: true,
    });
    const { data: googleAnalyticsAccountNames } = useGetListAccountNamesQuery({}, {
        skip: integrationId !== 'googleanalytics',
        refetchOnMountOrArgChange: true,
    })

    const [fetched, setFetched] = useState("");
    const [listYoutube, { data: listYoutubeData }] = useLazyListYoutubeQuery({});

    const { data: listGoogleAdsData } = useGetListGoogleAdsAccountNamesQuery({}, {
        skip: integrationId !== 'googleads',
        refetchOnMountOrArgChange: true,
    });

    const { data: listMetaAccounts } = useGetListMetaAccountsQuery({}, {
        skip: integrationId !== 'meta',
        refetchOnMountOrArgChange: true,
    });

    const safeIntegrationId = typeof integrationId === 'string' && integrationId.includes('custom-') ? integrationId : '';
    const integration_tool = safeIntegrationId
        ? safeIntegrationId.replace('custom-', '').charAt(0).toUpperCase() + safeIntegrationId.replace('custom-', '').slice(1)
        : '';

    const accountNames = googleAnalyticsAccountNames?.account_names || listYoutubeData?.[0]?.title || listGoogleAdsData || listMetaAccounts || [];

    const [getProductID, { data: productIDList, isFetching: isProuctIDListLoading }] = useLazyGetAccountNamesAfterSelectionQuery({});

    const [updateBigQueryIntegrationDataset] = useUpdateBigQueryIntegrationDatasetMutation();

    const [updateTableSchemaDesc] = useUpdateTableSchemaDescMutation();

    const [skipSchema, setSkipSchema] = useState(integrationId !== 'custom-integration');


    const listAnalytics = productIDList?.properties || [];

    const methods = useForm({
        defaultValues: {
            property_id: listAnalytics.length ? listAnalytics[0] : '',
            account_name: '',
            table_name: '',
        },
        mode: 'onChange',
    });

    const { watch, setValue, control, handleSubmit } = methods;

    // Watch for changes in property_id
    const accountName = watch('account_name');
    const propertyId = watch('property_id');
    const tableName = watch('table_name');

    const getAccountNames = React.useCallback(async (accountName: string) => {
        try {
            if (accountName && integrationId === 'googleanalytics') {
                await getProductID(accountName);
            } else {
                setValue('property_id', null);
            }
        } catch (error) {
            console.error("Error fetching account names:", error);
        }
    }, [propertyId]);

    useEffect(() => {
        getAccountNames(accountName);
    }, [accountName, getAccountNames]);

    // download data
    const [downloadGAData, { isLoading: isDownloadingGA }] = useLazyDownloadAnalyticsDataQuery();
    const [downloadGoogleAdsData, { isLoading: isDownloadingGoogleAds }] = useLazyDownloadGoogleAdsDataQuery({});
    const [downloadShopifyData, { isLoading: isDownloadingShopify }] = useLazyDownloadShopifyDataQuery({});
    const [downloadMetaData, { isLoading: isDownloadingMeta }] = useLazyDownloadMetaDataQuery({});

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total_count: 0,
    });


    // Fetch data conditionally
    const { data: getGAData = null, isFetching: isGAFetching, isError: isGAError, isSuccess: isGADataSuccess, error: gaError } = useGetAnalyticsDataQuery(
        { property_id: propertyId },
        { skip: !propertyId || integrationId !== 'googleanalytics' }
    );

    const { data: getTableNameAfterSchema, isLoading: fetchTableNameAfterSchema } = useGetTableNameAfterViewSchemaQuery("", {
        skip: !integration_tool,
        refetchOnMountOrArgChange: true
    })

    // const { data: listTables } = useListTablesInDatasetQuery({
    //     integration_tool
    // },
    //     {
    //         skip: !integration_tool,
    //     }
    // )

    const { data: checkSchemaStatus } = useCheckSchemaDescQuery("", {
        skip: skipSchema,
        pollingInterval: 5000,
    })


    useEffect(() => {
        if (integrationId === 'custom-integration' && integration_tool) {
            if (!checkSchemaStatus?.status || checkSchemaStatus?.status === "pending") {
                setSkipSchema(false)
            } else {
                setSkipSchema(true)
            }
        }
    }, [checkSchemaStatus?.status, integrationId])


    const { data: tableSchema, isLoading: isLoadingTableSchema, isFetching: isFetchingTableSchema, isError: isErrorTableSchema, error: errorTableSchema } = useGetTableSchemaFromDbQuery({
        tableName
    }, {
        skip: !tableName || !skipSchema || integrationId === 'shopify',
        refetchOnMountOrArgChange: true
    });


    // const { data: tableSchema, isLoading: isLoadingTableSchema, isFetching: isFetchingTableSchema, isError: isErrorTableSchema, error: errorTableSchema } = useGetTableSchemaQuery({
    //     integration_tool,
    //     table_id: tableName,
    // }, {
    //     skip: !tableName || !integration_tool,
    //     refetchOnMountOrArgChange: true
    // });

    const { data: getShopifyData = null, isFetching: isShopifyFetching, isError: isShopifyError, error: shopifyError, isSuccess: isShopifyDataSuccess, refetch: shopifyRefetch } = useGetShopifyDataQuery({
        table_name: tableName,
        page: pagination.page,
        limit: pagination.limit,
    }, {
        skip: !!integration_tool || !tableName || integrationId !== 'shopify' || !getStatus?.shopify,
    });

    const { data: getMetaData = null, isFetching: isMetaFetching, isError: isMetaError, error: metaError, isSuccess: isMetaDataSuccess, } = useGetMetaDataQuery(
        { account_id: accountName },
        {
            skip: integrationId !== 'meta' || !getStatus?.meta || !accountName,
        });

    const { data: getYoutubeData = null, isFetching: isYoutubeFetching, isError: isYoutubeError, error: youtubeError, isSuccess: isYoutubeDataSuccess } = useGetYoutubeDataQuery('',
        { skip: integrationId !== 'youtube' || !getStatus?.youtube },
    )

    const { data: getGoogleAdsData = null, isFetching: isGoogleAdsFetching, isError: isGoogleAdsError, error: googleAdsError, isSuccess: isGoogleAdsDataSuccess } = useGetGoogleAdsDataQuery(
        { customerId: accountName },
        { skip: integrationId !== 'googleads' || !getStatus?.googleads || !accountName });

    const getShopifyUser = localStorage.getItem("oogw.ai_shopify_email");
    const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";

    const skipShopify = !(tokenType === "Shopify" && fetched !== "Fetched" && tableName);


    const { data: getShopifyStatus } = useGetShopifyStatusQuery(getShopifyUser, {
        skip: skipShopify,
        pollingInterval: 5000,
    });

    // Keep updating the fetched value whenever status changes
    useEffect(() => {
        const status = getShopifyStatus?.status;
        if (status) {
            setFetched(status);
        }
    }, [getShopifyStatus?.status, tableName]);

    // console.log("skipShopify",skipShopify,fetched);

    // http://localhost:8000/shopify/token?token=U2FsdGVkX1/QYv5fU/JPwgobOpBGJ+KfVJkC3sbHqD9njWfK3yrsX0mCI1NE9hpXFynFfuK+kIMLQZFr8yFZIQ==&email=U2FsdGVkX1/wmJimspPdQ6YI1BGzfH7AcY+StxCF/lM=&shop-domain=oogwaiitest.myshopify.com

    const handleTableChange = (newPagination: any) => {
        setPagination((prev) => ({
            ...prev,
            page: newPagination.page || prev.page,
            limit: newPagination.limit || prev.limit,
            total_count: newPagination.total_count || prev.total_count,
        }));
        shopifyRefetch();
    };

    useEffect(() => {
        if (integrationId === 'youtube') {
            listYoutube({})
        }
    }, [integrationId, listYoutube])

    const getData = useMemo(() => {
        const handleTableData = (): {
            data: any[];
            pagination?: {
                page: number;
                limit: number;
                total_count: number;
            };
            isLoading: boolean;
            isError: boolean;
            isFetching: boolean;
            isSuccess: boolean;
        } => {
            try {
                if (integration_tool) {
                    if (isErrorTableSchema) {
                        throw errorTableSchema;
                    }
                    return {
                        data: isErrorTableSchema ? [] : tableSchema?.schema || [],
                        isLoading: isFetchingTableSchema,
                        isError: isErrorTableSchema,
                        isFetching: isFetchingTableSchema,
                        isSuccess: isLoadingTableSchema,
                    };
                }

                if (integrationId === 'googleanalytics') {
                    if (isGAError) {
                        throw gaError;
                    }
                    return {
                        data: isGAError ? [] : getGAData?.data || [],
                        isLoading: isGAFetching,
                        isError: isGAError,
                        isFetching: isGAFetching,
                        isSuccess: isGADataSuccess,
                    };
                }

                if (integrationId === 'googleads') {
                    if (isGoogleAdsError) {
                        throw googleAdsError;
                    }
                    return {
                        data: isGoogleAdsError ? [] : getGoogleAdsData?.data || [],
                        isLoading: isGoogleAdsFetching,
                        isError: isGoogleAdsError,
                        isFetching: isGoogleAdsFetching,
                        isSuccess: isGoogleAdsDataSuccess,
                    };
                }

                if (integrationId === 'shopify') {
                    if (isShopifyError) {
                        throw shopifyError;
                    }
                    return {
                        data: isShopifyError ? [] : (getShopifyData?.data ?? []),
                        pagination: {
                            page: getShopifyData?.page ?? 1,
                            limit: getShopifyData?.limit ?? 10,
                            total_count: getShopifyData?.total_count ?? 0
                        },
                        isLoading: isShopifyFetching,
                        isError: isShopifyError,
                        isFetching: isShopifyFetching,
                        isSuccess: isShopifyDataSuccess,
                    };
                }

                if (integrationId === 'meta') {
                    if (isMetaError) {
                        throw metaError;
                    }
                    return {
                        data: isMetaError ? [] : getMetaData?.data || [],
                        isLoading: isMetaFetching,
                        isError: isMetaError,
                        isFetching: isMetaFetching,
                        isSuccess: isMetaDataSuccess,
                    };
                }

                if (integrationId === 'youtube') {
                    if (isYoutubeError) {
                        throw youtubeError;
                    }
                    return {
                        data: isYoutubeError ? [] : getYoutubeData || [],
                        isLoading: isYoutubeFetching,
                        isError: isYoutubeError,
                        isFetching: isYoutubeFetching,
                        isSuccess: isYoutubeDataSuccess,
                    };
                }

                return {
                    data: [],
                    isLoading: false,
                    isError: false,
                    isFetching: false,
                    isSuccess: false,
                };
            } catch (error: any) {
                console.error('Error fetching integration data:', error);
                toast.error(error?.data?.error || 'Unexpected error fetching integration data', { toastId: 'analytics-loading-failed' });
                return {
                    data: [],
                    isLoading: false,
                    isError: true,
                    isFetching: false,
                    isSuccess: false,
                };
            }
        };

        return handleTableData();
    }, [
        integrationId,
        fetched,
        // 
        isGAError,
        getGAData,
        gaError,
        isGAFetching,
        // 
        isGoogleAdsError,
        getGoogleAdsData,
        googleAdsError,
        isGoogleAdsFetching,
        //
        isShopifyError,
        shopifyError,
        getShopifyData,
        isShopifyFetching,
        // 
        getMetaData,
        metaError,
        isMetaError,
        isMetaFetching,
        // 
        isYoutubeError,
        youtubeError,
        getYoutubeData,
        isYoutubeFetching,
        // success
        isYoutubeDataSuccess,
        isGADataSuccess,
        isShopifyDataSuccess,
        isMetaDataSuccess,
        isGoogleAdsDataSuccess,
        //
        isErrorTableSchema,
        tableSchema,
        isFetchingTableSchema,
        isLoadingTableSchema,
        integration_tool
    ]);

    const handleDownload = async () => {
        if (integrationId === 'googleanalytics') {
            if (!propertyId) {
                toast.error('Please select a property ID to download data');
                return;
            }
            const payload = { property_id: propertyId };
            try {
                const link = document.createElement('a');
                const response = await downloadGAData(payload);
                if (response.data instanceof Blob) {
                    link.href = URL.createObjectURL(response.data);
                } else {
                    throw new Error('Failed to download data');
                }
                link.setAttribute('download', `analytics_data_${propertyId}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                toast.success('Data downloaded successfully');
            } catch (error) {
                console.error(error);
                toast.error('Error downloading analytics data');
            }
        }
        if (integrationId === 'googleads') {
            if (!accountName) {
                toast.error('Please select a company name to download data');
                return;
            }
            const payload = { customerId: accountName };
            try {
                const link = document.createElement('a');
                const response = await downloadGoogleAdsData(payload);
                if (response.data instanceof Blob) {
                    link.href = URL.createObjectURL(response.data);
                } else {
                    throw new Error('Failed to download data');
                }
                link.setAttribute('download', `analytics_data_${accountName}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                toast.success('Data downloaded successfully');
            } catch (error) {
                console.error(error);
                toast.error('Error downloading analytics data');
            }
        }
        if (integrationId === 'shopify') {
            try {
                const link = document.createElement('a');
                const response = await downloadShopifyData({ property_id: '' });
                if (response.data instanceof Blob) {
                    link.href = URL.createObjectURL(response.data);
                } else {
                    throw new Error('Failed to download data');
                }
                link.setAttribute('download', `analytics_data_${propertyId}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                toast.success('Data downloaded successfully');
            } catch (error) {
                console.error(error);
                toast.error('Error downloading analytics data');
            }

        }

        if (integrationId === 'meta') {
            try {
                const link = document.createElement('a');
                const response = await downloadMetaData({ account_id: accountName });
                if (response.data instanceof Blob) {
                    link.href = URL.createObjectURL(response.data);
                } else {
                    throw new Error('Failed to download data');
                }
                link.setAttribute('download', `analytics_data_${accountName}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                toast.success('Data downloaded successfully');
            } catch (error) {
                console.error(error);
                toast.error('Error downloading analytics data');
            }
        }

        if (integrationId !== 'googleads' && integrationId !== 'shopify' && integrationId !== 'meta') {
            toast.error(`${(integrationId ?? 'unknown').charAt(0).toUpperCase() + (integrationId ?? 'unknown').slice(1)} is not supported for download`);
        }

    };

    if (!integration_tool && (!getStatus || !integrationId || !getStatus[integrationId])) {
        return (
            <div className="flex flex-col items-center">
                <p className="text-2xl font-semibold text-gray-500">No Data Found</p>
                <p className="text-sm font-normal text-gray-400">Please check your integration status</p>
            </div>
        );
    }

    const { isError, isFetching } = getData ?? {};

    const handleSaveBigQueryData = async (data: any) => {
        try {
            const formData = data?.data;
            const originalData = getData?.data || [];

            const fields = originalData.map((item: any, index: number) => {
                const formItem = formData?.[index] || {};

                return {
                    name: item.name,
                    description:
                        formItem?.description !== undefined && formItem?.description !== null
                            ? formItem.description
                            : item.description || ''
                };
            });

            const payload = {
                table_name: tableName,
                schema: fields
            };

            // const res = await updateBigQueryIntegrationDataset(payload).unwrap();
            const res = await updateTableSchemaDesc(payload).unwrap();

            if (res?.error) {
                throw new Error(res.error);
            }

            toast.success('Event Data Saved Successfully');
        } catch (err) {
            console.log(err);
            toast.error(`Error saving event data ${(err as { data?: { error?: string } })?.data?.error || ''}`);
        }
    };

    const items: MenuProps['items'] =
        typeof accountNames === 'string'
            ? []
            : (accountNames as any[]).map((item) => {
                const isObject = typeof item === 'object' && item !== null;
                const label = isObject ? item.name : item;
                const valueToSet = isObject ? item.customer_id || item.account_id : item;

                return {
                    label: (
                        <button
                            className="font-medium"
                            onClick={(e) => {
                                setValue('account_name', valueToSet, { shouldValidate: true });
                                e.preventDefault();
                            }}
                        >
                            {label}
                        </button>
                    ),
                    key: label,
                    onClick: () => setValue('account_name', valueToSet, { shouldValidate: true }),
                };
            }) || [];

    const selectedName = (() => {
        try {
            if (typeof accountNames === 'string') return accountNames;
            if (Array.isArray(accountNames)) {
                const match = accountNames.find((item) =>
                    typeof item === 'object' &&
                    (item?.customer_id === accountName || item?.account_id === accountName)
                );
                return match?.name ?? accountName;
            }
            return accountName;
        } catch (error) {
            console.error('Error determining selected name:', error);
            return accountName;
        }
    })();

    return (
        <div>
            <div className="flex justify-between h-12">
                <div>
                    {
                        integration_tool ? (
                            <h4 className="flex gap-2 items-center">
                                Custom Schema
                                {getData?.data?.length > 0 && (
                                    <span className="text-sm font-normal text-gray-500">
                                        ({getData.pagination?.total_count || getData.data.length} records)
                                    </span>
                                )}
                            </h4>
                        ) : typeof accountNames === 'string' ? (
                            <h4 className="flex gap-2 items-center">
                                {accountNames}
                                {getData?.data?.length > 0 && (
                                    <span className="text-sm font-normal text-gray-500">
                                        ({getData.pagination?.total_count || getData.data.length} records)
                                    </span>
                                )}
                            </h4>
                        ) : Array.isArray(accountNames) && accountNames.length ? (
                            <Dropdown
                                menu={{ items }}
                                trigger={['click']}
                                disabled={isFetching}
                                overlayClassName="border border-gray-200 rounded-lg shadow-none"
                            >
                                <span className="bg-transparent border-none p-0 m-0 cursor-pointer">
                                    <Space>
                                        <h4 className="flex gap-2 items-center">
                                            {selectedName || 'Select Company Name'}{' '}
                                            <ChevronOutlineRightIcon className="rotate-90" />
                                            {getData?.data?.length > 0 && (
                                                <span className="text-sm font-normal text-gray-500 ml-2">
                                                    ({getData.pagination?.total_count || getData.data.length} records)
                                                </span>
                                            )}
                                        </h4>
                                    </Space>
                                </span>
                            </Dropdown>
                        ) : (
                            <h4 className="flex gap-2 items-center">
                                Analytics Data
                                {getData?.data?.length > 0 && (
                                    <span className="text-sm font-normal text-gray-500">
                                        ({getData.pagination?.total_count || getData.data.length} records)
                                    </span>
                                )}
                            </h4>
                        )}
                    <p className="text-[#535862] text-sm font-normal">To get Oogwai to answer your questions, allow us to fetch data from the sources below</p>
                </div>

                {!!(getData.data.length || Object.keys(getData.data).length) &&
                    <Button
                        loading={isDownloadingGA || isDownloadingShopify || isDownloadingMeta || isDownloadingGoogleAds}
                        disabled={isFetching || isError}
                        onClick={handleDownload}
                        icon={<DownloadIcon />}
                        iconPosition="end"
                    >
                        <span className="flex items-center gap-2"><DownloadExcel />Complete Data</span>
                    </Button>
                }
            </div>
            <div className="my-3 flex gap-2 items-center">
                {integrationId === 'googleanalytics' && (accountName) && (
                    <>
                        <span className="text-[#535862] text-md font-normal">Property Id</span>
                        <Select
                            className="min-w-36 ml-2"
                            onChange={(value) => setValue('property_id', value, { shouldValidate: true })}
                            placeholder="Select Property Id"
                            disabled={isFetching}
                            loading={isProuctIDListLoading}
                            allowClear
                            options={listAnalytics?.map((item: { property_name: String, property_id: Number }) => ({
                                value: item?.property_id,
                                label: `${item?.property_name} - ${item?.property_id}`,
                            }))}
                        />

                    </>
                )}
                {(integrationId === 'shopify') && (
                    <>
                        <span className="text-[#535862] text-md font-normal">Table Name</span>
                        <Select
                            className="min-w-36 ml-2"
                            onChange={(value) => {
                                setValue('table_name', value, { shouldValidate: true });
                                setFetched("")
                                if (tableName) {
                                    shopifyRefetch()
                                }
                            }}
                            placeholder="Select Table Name"
                            disabled={isFetching}
                            options={[
                                { value: 'customers', label: 'customers' },
                                { value: 'orders', label: 'orders' },
                                { value: 'products', label: 'products' },
                                { value: 'abandonedCheckouts', label: 'abandonedCheckouts' },
                                { value: 'collections', label: 'collections' },
                            ]}
                        />

                    </>
                )}
                {(integration_tool) && (
                    <>
                        <span className="text-[#535862] text-md font-normal">Table Name</span>
                        <Select
                            className="min-w-36 ml-2"
                            onChange={(value) => setValue('table_name', value, { shouldValidate: true })}
                            placeholder="Select Table Name"
                            disabled={fetchTableNameAfterSchema}
                            options={getTableNameAfterSchema?.table_names?.map((item: any) => ({
                                value: item,
                                label: item,
                            }))}
                        />

                    </>
                )}
            </div>
            <>
                {(isError || isFetching || (integrationId === 'googleanalytics' && !propertyId) || (integrationId === 'googleads' && !accountName)) ? (
                    <div className="flex flex-col items-center justify-center">
                        {isFetching ? (
                            <div className="flex flex-col items-center justify-center fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                <p className="text-2xl font-semibold text-gray-500">{getShopifyStatus?.status ?? "Loading data..."}</p>
                                <p className="text-sm font-normal text-gray-400">{'Please wait while we fetch the data'}</p>
                            </div>
                            // <Loading />
                        ) : integrationId === 'googleanalytics' ? (
                            <>
                                <p className="text-2xl font-semibold text-gray-500">Please select property ID</p>
                                <p className="text-sm font-normal text-gray-400">{'to fetch raw data'}</p>
                            </>
                        ) : integrationId === 'googleads' ? (
                            <>
                                <p className="text-2xl font-semibold text-gray-500">
                                    {googleAdsError
                                        ? typeof googleAdsError === 'string'
                                            ? googleAdsError
                                            : 'An error occurred'
                                        : 'Please select Company name'}
                                </p>
                                <p className="text-sm font-normal text-gray-400">{'to fetch raw data'}</p>
                            </>
                        ) : (
                            <>
                                <p className="text-2xl font-semibold text-gray-500">Error fetching data</p>
                                <p className="text-sm font-normal text-gray-400">{'Please check your connection and try again'}</p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="mb-14 overflow-y-auto">
                            {getData?.data?.length > 0 ? <DynamicTable
                                data={getData?.data}
                                control={integration_tool ? control : undefined}
                                fieldConfig={integration_tool ?
                                    {
                                        "oogwai_event_name": {
                                            "type": "input",
                                            "placeholder": "Enter event name",
                                        },
                                        "Description": {
                                            "type": "text-area",
                                            "placeholder": "Enter a description...",
                                        },
                                    } : undefined
                                }
                            /> : getData?.data?.length === 0 && tableName && getShopifyStatus?.status !== "Fetched" ? <p className="text-2xl font-semibold text-gray-500 text-center">{getShopifyStatus?.status ?? "Syncing data..."}</p> :
                                integration_tool && checkSchemaStatus?.status === "pending" ? <p className="text-2xl font-semibold text-gray-500 text-center">{"Syncing data..."}</p> :
                                    <DynamicTable
                                        data={getData?.data}
                                        control={integration_tool ? control : undefined}
                                        fieldConfig={integration_tool ?
                                            {
                                                "oogwai_event_name": {
                                                    "type": "input",
                                                    "placeholder": "Enter event name",
                                                },
                                                "Description": {
                                                    "type": "text-area",
                                                    "placeholder": "Enter a description...",
                                                },
                                            } : undefined
                                        }
                                    />}

                            {integration_tool && skipSchema &&
                                <div className="fixed bottom-0 left-0 w-full bg-white border-t flex items-center justify-center py-2">
                                    <Button
                                        type='primary'
                                        disabled={isFetching}
                                        onClick={handleSubmit(handleSaveBigQueryData)}
                                    >
                                        Save Settings
                                    </Button>
                                </div>
                            }
                        </div>
                        {getShopifyData?.data?.length > 0 && (
                            <div className="fixed bottom-0 left-0 w-full bg-white border-t flex items-center p-4">
                                <Pagination
                                    showSizeChanger
                                    current={pagination.page}
                                    pageSize={pagination.limit}
                                    onChange={(page, pageSize) => handleTableChange({ page, limit: pageSize })}
                                    total={getData?.pagination?.total_count}
                                />
                            </div>
                        )}
                    </>
                )}
                {/* {!isError && <div className="fixed bottom-0 left-0 w-full bg-white shadow-md p-4 mx-auto">
                    <FakeProgressBar isLoading={isFetching} isSuccess={isSuccess} />
                </div>} */}
            </>
        </div>
    )
};

const ViewRawDataUnauthorized = () => {
    const location = useLocation();
    const { email } = location.state || {}

    const {
        data: getShopifyData,
        isLoading: isShopifyFetching,
        isError: isShopifyError,
        isSuccess: isShopifyDataSuccess
    } = useGetShopifyUserDataQuery({ email })
    const getData = useMemo(() => {
        const handleTableData = (): {
            data: [];
            isLoading: boolean;
            isError: boolean;
            isFetching: boolean;
            isSuccess: boolean
        } => {

            try {
                return {
                    data: isShopifyError ? [] : getShopifyData || [],
                    isLoading: isShopifyFetching,
                    isError: isShopifyError,
                    isFetching: isShopifyFetching,
                    isSuccess: isShopifyDataSuccess
                };
            } catch (error: any) {
                toast.error(error?.data?.error || 'Unexpected error fetching integration data', { toastId: 'analytics-loading-failed' });
                return {
                    data: [],
                    isLoading: false,
                    isError: true,
                    isFetching: false,
                    isSuccess: false
                };
            }
        };

        return handleTableData();
    }, [
        isShopifyFetching,
        isShopifyError,
        isShopifyDataSuccess,
        getShopifyData
    ]);

    const { isError, isFetching } = getData ?? {};

    if (!getData?.data) {
        return (
            <div className="flex flex-col items-center">
                <p className="text-2xl font-semibold text-gray-500">No Data Found</p>
                <p className="text-sm font-normal text-gray-400">Please check your integration status</p>
                <Button
                    type="primary"
                    onClick={() => window.location.href = '/login'}
                    className="mt-4"
                >
                    Go to Login Page
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col p-4 space-y-4">
            <div className="mb-4">
                <Button
                    type="link"
                    onClick={() => window.history.back()}
                    className="flex items-center space-x-2 text-gray-700"
                >
                    <ChevronOutlineRightIcon className="w-4 h-4 transform rotate-180" />
                    <span>Back</span>
                </Button>
            </div>
            <div>
                <h4 className="flex gap-2 items-center">
                    Shopify Analytics Data
                    {getData?.data?.length > 0 && (
                        <span className="text-sm font-normal text-gray-500">
                            ({getData.data.length} records)
                        </span>
                    )}
                </h4>
                <p className="text-[#535862] text-sm font-normal">To get Oogwai to answer your questions, allow us to fetch data from the sources below</p>
            </div>
            <>
                {(isError || isFetching) ? (
                    <div className="flex flex-col items-center justify-center">
                        {isFetching ? (
                            <div className="flex flex-col items-center justify-center fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                <p className="text-2xl font-semibold text-gray-500">Loading data...</p>
                                <p className="text-sm font-normal text-gray-400">{'Please wait while we fetch the data'}</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-2xl font-semibold text-gray-500">Error fetching data</p>
                                <p className="text-sm font-normal text-gray-400">{'Please check your connection and try again'}</p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <DynamicTable data={getData?.data} />
                        <Pagination
                            showSizeChanger
                            // onShowSizeChange={onShowSizeChange}
                            defaultCurrent={3}
                            total={500}
                        // onChange={handleTableChange}
                        />
                    </>
                )}
                {/* {!isError && <div className="fixed bottom-0 left-0 w-full bg-white shadow-md p-4 mx-auto">
                    <FakeLoadingBar isLoading={isFetching} isSuccess={isSuccess} />
                </div>} */}
            </>
        </div>
    )
}

export { ViewRawDataUnauthorized }

export default ViewRawData;
